import React, { useState } from 'react';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { autoFormatField, parseDisplayValue } from '../../utils/valueFormatters';
import { 
  BarChart3,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Edit3,
  Search,
  Filter,
  Download,
  Upload
} from 'lucide-react';

const CollegeBatchDataUpdate = ({ isOpen, onClose, prefilterCollege = null }) => {
  const { colleges, updateCollege } = useCollegeContext();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchMode, setSearchMode] = useState('colleges'); // 'colleges' or 'fields'
  const [collegeSearchTerm, setCollegeSearchTerm] = useState(prefilterCollege ? prefilterCollege.name : '');
  const [fieldSearchTerm, setFieldSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('basic'); // basic, rankings, test_scores, acceptance, financial, essays, deadlines, outcomes
  const [editingColleges, setEditingColleges] = useState(new Map());
  const [saving, setSaving] = useState(false);
  const [saveResults, setSaveResults] = useState(null);

  // Tab configuration for different data categories
  const dataTabs = {
    basic: {
      label: 'Basic Info',
      icon: '🏫',
      fields: [
        { key: 'location', label: 'Location', type: 'text', placeholder: 'City, State' },
        { key: 'academic_focus', label: 'Academic Focus', type: 'select', options: ['Engineering', 'Nursing', 'Engineering, Nursing', 'Liberal Arts', 'Business', 'Sciences', 'Research', 'Pre-Med', 'Technology', 'General'] },
        { key: 'strategic_classification', label: 'Classification', type: 'select', options: ['Highly Competitive', 'Very Competitive', 'Moderately Competitive', 'Less Competitive'] },
        { key: 'application_round', label: 'App Round', type: 'select', options: [{ value: 'early_action', label: 'Early Action' }, { value: 'early_decision', label: 'Early Decision' }, { value: 'regular_decision', label: 'Regular Decision' }, { value: 'rolling', label: 'Rolling' }] },
        { key: 'application_status', label: 'App Status', type: 'select', options: [{ value: 'planning', label: 'Planning' }, { value: 'in_progress', label: 'In Progress' }, { value: 'submitted', label: 'Submitted' }, { value: 'completed', label: 'Completed' }] },
        { key: 'visited', label: 'Visited', type: 'checkbox' }
      ]
    },
    rankings: {
      label: 'Rankings',
      icon: '🏆',
      fields: [
        { key: 'ranking', label: 'USNWR Ranking', type: 'text', placeholder: '#7' },
        { key: 'engineering_ranking', label: 'Engineering Ranking', type: 'text', placeholder: '#10' },
        { key: 'nursing_ranking', label: 'Nursing Ranking', type: 'text', placeholder: '#15' },
        { key: 'fit_score', label: 'Fit Score', type: 'text', placeholder: '8.5' }
      ]
    },
    acceptance: {
      label: 'Acceptance',
      icon: '📊',
      fields: [
        { key: 'acceptance_rate', label: 'Overall Rate', type: 'text', placeholder: '6%' },
        { key: 'engineering_acceptance_rate', label: 'Engineering Rate', type: 'text', placeholder: '4.2%' },
        { key: 'ed_acceptance_rate', label: 'Early Decision Rate', type: 'text', placeholder: '8%' },
        { key: 'ea_acceptance_rate', label: 'Early Action Rate', type: 'text', placeholder: '7%' },
        { key: 'rd_acceptance_rate', label: 'Regular Decision Rate', type: 'text', placeholder: '5%' },
        { key: 'yield_rate', label: 'Yield Rate', type: 'text', placeholder: '25%' },
        { key: 'application_strategy', label: 'Strategy', type: 'select', options: ['Early Decision Advantage', 'Early Action Recommended', 'Regular Decision Recommended', 'Rolling Admission'] }
      ]
    },
    test_scores: {
      label: 'Test Scores',
      icon: '📝',
      fields: [
        { key: 'sat_range', label: 'SAT Range', type: 'text', placeholder: '1480-1570' },
        { key: 'sat_math_range', label: 'SAT Math Range', type: 'text', placeholder: '750-800' },
        { key: 'sat_reading_range', label: 'SAT Reading Range', type: 'text', placeholder: '730-770' },
        { key: 'act_range', label: 'ACT Range', type: 'text', placeholder: '33-35' },
        { key: 'test_optional', label: 'Test Optional', type: 'checkbox' },
        { key: 'test_blind', label: 'Test Blind', type: 'checkbox' },
        { key: 'super_scoring', label: 'Super Scoring', type: 'checkbox' },
        { key: 'ap_ib_credit_policy', label: 'AP/IB Credit Policy', type: 'text', placeholder: 'Liberal credit policy' }
      ]
    },
    academics: {
      label: 'Academic Info',
      icon: '🎓',
      fields: [
        { key: 'avg_gpa_weighted', label: 'Avg GPA (Weighted)', type: 'text', placeholder: '4.18' },
        { key: 'avg_gpa_unweighted', label: 'Avg GPA (Unweighted)', type: 'text', placeholder: '3.95' },
        { key: 'class_rank_importance', label: 'Class Rank Importance', type: 'select', options: ['Very Important', 'Important', 'Considered', 'Not Considered'] },
        { key: 'grad_rate_4yr', label: '4-Year Grad Rate', type: 'text', placeholder: '88%' },
        { key: 'grad_rate_6yr', label: '6-Year Grad Rate', type: 'text', placeholder: '95%' },
        { key: 'retention_rate', label: 'Retention Rate', type: 'text', placeholder: '97%' }
      ]
    },
    financial: {
      label: 'Financial',
      icon: '💰',
      fields: [
        { key: 'tuition', label: 'Tuition', type: 'text', placeholder: '$60,000' },
        { key: 'tuition_in_state', label: 'In-State Tuition', type: 'text', placeholder: '$12,000' },
        { key: 'tuition_out_of_state', label: 'Out-of-State Tuition', type: 'text', placeholder: '$60,000' },
        { key: 'room_board', label: 'Room & Board', type: 'text', placeholder: '$16,000' },
        { key: 'total_cost', label: 'Total Cost', type: 'text', placeholder: '$76,000' },
        { key: 'avg_need_aid', label: 'Avg Need-Based Aid', type: 'text', placeholder: '$50,000' },
        { key: 'avg_merit_aid', label: 'Avg Merit Aid', type: 'text', placeholder: '$15,000' },
        { key: 'percent_receiving_aid', label: '% Receiving Aid', type: 'text', placeholder: '60%' },
        { key: 'need_blind', label: 'Need Blind', type: 'checkbox' },
        { key: 'meets_full_need', label: 'Meets Full Need', type: 'checkbox' },
        { key: 'no_loan_policy', label: 'No Loan Policy', type: 'checkbox' }
      ]
    },
    deadlines: {
      label: 'Deadlines',
      icon: '📅',
      fields: [
        { key: 'ed_deadline', label: 'Early Decision Deadline', type: 'date' },
        { key: 'ed2_deadline', label: 'Early Decision II Deadline', type: 'date' },
        { key: 'ea_deadline', label: 'Early Action Deadline', type: 'date' },
        { key: 'rd_deadline', label: 'Regular Decision Deadline', type: 'date' },
        { key: 'finaid_deadline', label: 'Financial Aid Deadline', type: 'date' },
        { key: 'css_deadline', label: 'CSS Profile Deadline', type: 'date' },
        { key: 'merit_deadline', label: 'Merit Scholarship Deadline', type: 'date' },
        { key: 'honors_deadline', label: 'Honors Program Deadline', type: 'date' }
      ]
    },
    campus_life: {
      label: 'Campus & Culture',
      icon: '🏛️',
      fields: [
        { key: 'college_type', label: 'College Type', type: 'select', options: ['Private', 'Public', 'Private Non-profit', 'Public Research', 'Liberal Arts'] },
        { key: 'size_category', label: 'Size Category', type: 'select', options: ['Small (<3,000)', 'Medium (3,000-15,000)', 'Large (15,000+)'] },
        { key: 'student_enrollment', label: 'Student Enrollment', type: 'number', placeholder: '6500' },
        { key: 'campus_setting', label: 'Campus Setting', type: 'select', options: ['Urban', 'Suburban', 'Rural', 'City'] },
        { key: 'religious_affiliation', label: 'Religious Affiliation', type: 'text', placeholder: 'Methodist, Catholic, etc.' },
        { key: 'student_faculty_ratio', label: 'Student:Faculty Ratio', type: 'text', placeholder: '6:1' },
        { key: 'percent_international', label: '% International Students', type: 'text', placeholder: '12%' },
        { key: 'percent_out_of_state', label: '% Out-of-State', type: 'text', placeholder: '85%' },
        { key: 'avg_class_size', label: 'Average Class Size', type: 'number', placeholder: '19' }
      ]
    },
    outcomes: {
      label: 'Career Outcomes',
      icon: '💼',
      fields: [
        { key: 'avg_salary', label: 'Average Starting Salary', type: 'text', placeholder: '$75,000' },
        { key: 'employment_rate', label: 'Employment Rate', type: 'text', placeholder: '94%' },
        { key: 'graduate_school_placement', label: 'Graduate School Placement', type: 'textarea', placeholder: 'High placement rates in top programs...' },
        { key: 'alumni_network', label: 'Alumni Network', type: 'textarea', placeholder: 'Strong network in finance, tech...' },
        { key: 'career_services', label: 'Career Services', type: 'textarea', placeholder: 'Comprehensive career support...' }
      ]
    },
    strategic: {
      label: 'Strategic Intelligence',
      icon: '🧠',
      fields: [
        { key: 'institutional_priorities', label: 'What This School Values', type: 'textarea', placeholder: 'Academic excellence, research innovation, community service...' },
        { key: 'student_advantages', label: 'Student Advantages', type: 'jsonb', placeholder: 'Strong Engineering Background, Alumni Parent' },
        { key: 'application_priorities', label: 'App Priorities', type: 'jsonb', placeholder: 'Engineering, Community, Leadership' },
        { key: 'recommended_strategy', label: 'Strategy Notes', type: 'textarea', placeholder: 'Strategic recommendations...' },
        { key: 'competitiveness_score', label: 'Competitiveness', type: 'select', options: ['Strong Match', 'Reach', 'Safety', 'High Reach'] }
      ]
    },
    essays_apps: {
      label: 'Essays & Apps',
      icon: '📝',
      fields: [
        { key: 'num_essays', label: 'Number of Essays', type: 'number', placeholder: '3' },
        { key: 'essay_prompts', label: 'Essay Prompts', type: 'jsonb', placeholder: 'Why Duke?, Community Service Essay' },
        { key: 'essay_limits', label: 'Essay Limits', type: 'jsonb', placeholder: '{"why_duke": 250, "community": 200}' },
        { key: 'video_essay_required', label: 'Video Essay Required', type: 'checkbox' },
        { key: 'portfolio_required', label: 'Portfolio Required', type: 'checkbox' },
        { key: 'app_platforms', label: 'App Platforms', type: 'jsonb', placeholder: 'Common App, Coalition' },
        { key: 'teacher_recs', label: 'Teacher Recommendations', type: 'number', placeholder: '2' },
        { key: 'counselor_rec_required', label: 'Counselor Rec Required', type: 'checkbox' },
        { key: 'interview_policy', label: 'Interview Policy', type: 'select', options: ['Required', 'Recommended', 'Optional', 'Not Available'] },
        { key: 'tracks_interest', label: 'Tracks Demonstrated Interest', type: 'checkbox' },
        { key: 'resume_required', label: 'Resume Required', type: 'checkbox' }
      ]
    }
  };

  // Search across all fields to find matching ones
  const searchFields = (searchTerm) => {
    if (!searchTerm) return [];
    
    const matchingFields = [];
    const searchLower = searchTerm.toLowerCase();
    
    Object.entries(dataTabs).forEach(([tabKey, tab]) => {
      tab.fields.forEach(field => {
        const fieldLabel = field.label.toLowerCase();
        const fieldKey = field.key.toLowerCase();
        const fieldPlaceholder = field.placeholder?.toLowerCase() || '';
        
        if (fieldLabel.includes(searchLower) || 
            fieldKey.includes(searchLower) || 
            fieldPlaceholder.includes(searchLower)) {
          matchingFields.push({
            ...field,
            tabKey,
            tabLabel: tab.label,
            tabIcon: tab.icon
          });
        }
      });
    });
    
    return matchingFields;
  };

  // Support concurrent college and field filtering
  const matchingFields = fieldSearchTerm ? searchFields(fieldSearchTerm) : [];
  
  // Get tabs that have matching fields
  const tabsWithMatches = [...new Set(matchingFields.map(field => field.tabKey))];

  const filteredColleges = colleges.filter(college => {
    // If we have a prefiltered college and no search term, only show that college
    if (prefilterCollege && !collegeSearchTerm) {
      return college.id === prefilterCollege.id;
    }
    
    // Filter by college name if college search term exists
    const matchesCollegeSearch = !collegeSearchTerm || 
      college.name.toLowerCase().includes(collegeSearchTerm.toLowerCase());
    
    // Filter by category
    const matchesCategory = filterCategory === 'all' || college.category === filterCategory;
    
    return matchesCollegeSearch && matchesCategory;
  }).sort((a, b) => {
    // If we have a prefiltered college, prioritize it at the top
    if (prefilterCollege) {
      if (a.id === prefilterCollege.id) return -1;
      if (b.id === prefilterCollege.id) return 1;
    }
    return a.name.localeCompare(b.name);
  });

  // Get fields to display - either from field search or current tab
  const fieldsToDisplay = fieldSearchTerm ? 
    matchingFields : 
    dataTabs[activeTab]?.fields || [];

  const handleFieldUpdate = (collegeId, field, value) => {
    // Auto-format the display value
    const formattedValue = autoFormatField(field, value);
    
    // Parse to storage value for database
    const storageValue = parseDisplayValue(field, formattedValue);
    
    setEditingColleges(prev => {
      const updated = new Map(prev);
      const collegeData = updated.get(collegeId) || {};
      // Store display value for UI, but we'll send storage value to database
      collegeData[field] = formattedValue;
      collegeData[`${field}_storage`] = storageValue; // Keep track of storage value
      updated.set(collegeId, collegeData);
      return updated;
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const results = {
      successful: [],
      failed: []
    };

    for (const [collegeId, updates] of editingColleges) {
      try {
        // Convert display values to storage values for database
        const storageUpdates = {};
        for (const [field, displayValue] of Object.entries(updates)) {
          if (field.endsWith('_storage')) {
            // Skip storage tracking fields
            continue;
          }
          
          // Check if we have a storage value tracked for this field
          const storageField = `${field}_storage`;
          if (updates[storageField] !== undefined) {
            storageUpdates[field] = updates[storageField];
          } else {
            // Fallback to parsing the display value
            storageUpdates[field] = parseDisplayValue(field, displayValue);
          }
        }

        await updateCollege(collegeId, storageUpdates, null, true); // Skip user check for batch updates
        const college = colleges.find(c => c.id === collegeId);
        results.successful.push(college?.name || collegeId);
      } catch (error) {
        const college = colleges.find(c => c.id === collegeId);
        results.failed.push({
          name: college?.name || collegeId,
          error: error.message
        });
      }
    }

    setSaveResults(results);
    setSaving(false);
    setEditingColleges(new Map());
  };

  const exportToCSV = () => {
    const csvHeaders = [
      'College Name',
      'Location', 
      'Category',
      'Ranking',
      'Acceptance Rate',
      'Engineering Ranking',
      'Engineering Acceptance Rate',
      'Tuition',
      'Academic Focus',
      'Strategic Classification',
      'Application Strategy',
      'Application Round',
      'Application Status',
      'Fit Score',
      'Deadline',
      'Early Deadline',
      'Visited',
      'Competitiveness Score',
      'Recommended Strategy'
    ];

    const csvRows = filteredColleges.map(college => [
      college.name || '',
      college.location || '',
      college.category || '',
      college.usnwr_ranking || '',
      college.acceptance_rate || '',
      college.engineering_ranking || '',
      college.engineering_acceptance_rate || '',
      college.tuition || '',
      college.academic_focus || '',
      college.strategic_classification || '',
      college.application_strategy || '',
      college.application_round || '',
      college.application_status || '',
      college.fit_score || '',
      college.deadline || '',
      college.early_deadline || '',
      college.visited ? 'Yes' : 'No',
      college.competitiveness_score || '',
      college.recommended_strategy || ''
    ]);

    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'college_data_export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getCollegeUpdates = (collegeId) => {
    return editingColleges.get(collegeId) || {};
  };

  const getValue = (college, field) => {
    const updates = getCollegeUpdates(college.id);
    return updates[field] !== undefined ? updates[field] : (college[field] || '');
  };

  // Render field input based on field type
  const renderFieldInput = (college, field) => {
    const value = getValue(college, field.key);
    const baseClasses = "w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-azure-500";

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.value)}
            className={baseClasses}
          />
        );
      
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.checked)}
            className="w-4 h-4 text-azure-600 border border-slate-300 rounded focus:ring-azure-500"
          />
        );
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.value)}
            className={baseClasses}
          >
            <option value="">Select...</option>
            {field.options.map(option => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : option.label;
              return (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className={baseClasses}
          />
        );
      
      case 'jsonb':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={2}
            className={`${baseClasses} font-mono text-xs`}
            title="Enter comma-separated values or JSON format"
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldUpdate(college.id, field.key, e.target.value)}
            className={baseClasses}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {prefilterCollege ? `Update ${prefilterCollege.name} Data` : 'Batch College Data Update'}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {prefilterCollege 
                ? `Editing information for ${prefilterCollege.name}. Clear search to see all colleges.`
                : 'Update college information across all schools efficiently'
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="space-y-4">
            {/* Search Inputs Row */}
            <div className="flex gap-4 items-center">
              {/* College Search */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-600 mb-1">Filter Schools</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search colleges..."
                    value={collegeSearchTerm}
                    onChange={(e) => setCollegeSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                  />
                </div>
              </div>

              {/* Field Search */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-600 mb-1">Filter Fields</label>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search fields (e.g., 'early', 'deadline', 'tuition')..."
                    value={fieldSearchTerm}
                    onChange={(e) => setFieldSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="w-48">
                <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-azure-500"
                >
                  <option value="all">All Categories</option>
                  <option value="reach">Reach Schools</option>
                  <option value="target">Target Schools</option>
                  <option value="safety">Safety Schools</option>
                  <option value="other">Other Schools</option>
                </select>
              </div>
            </div>

            {/* Search Results Summary */}
            <div className="flex items-center justify-between text-sm text-slate-600">
              <div className="flex gap-4">
                {collegeSearchTerm && (
                  <span>Schools: {filteredColleges.length} matches</span>
                )}
                {fieldSearchTerm && (
                  <span>
                    Fields: {matchingFields.length} match{matchingFields.length !== 1 ? 'es' : ''} 
                    {tabsWithMatches.length > 0 && (
                      <span> across {tabsWithMatches.length} tab{tabsWithMatches.length !== 1 ? 's' : ''}</span>
                    )}
                  </span>
                )}
              </div>
              {(collegeSearchTerm || fieldSearchTerm) && (
                <button
                  onClick={() => {
                    setCollegeSearchTerm('');
                    setFieldSearchTerm('');
                  }}
                  className="text-azure-600 hover:text-azure-700 text-xs"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {saveResults && (
          <div className="p-4 bg-azure-50 border-b border-azure-200">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="w-4 h-4" />
                {saveResults.successful.length} updated successfully
              </div>
              {saveResults.failed.length > 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  {saveResults.failed.length} failed to update
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Tabs - Scrollable */}
        {!fieldSearchTerm && (
          <div className="border-b border-slate-200">
            <nav className="-mb-px flex overflow-x-auto scrollbar-hide px-6">
              <div className="flex space-x-8 min-w-max">
                {Object.entries(dataTabs).map(([key, tab]) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === key
                        ? 'border-azure-500 text-azure-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        )}

        {/* Field Search Results Header */}
        {fieldSearchTerm && (
          <div className="border-b border-slate-200 px-6 py-4 bg-azure-50">
            <h3 className="text-lg font-semibold text-azure-900">
              Field Results: "{fieldSearchTerm}"
            </h3>
            <p className="text-sm text-azure-700 mt-1">
              Fields shown from multiple tabs. Clear field search to return to tab view.
            </p>
          </div>
        )}

        {/* Dynamic Data Grid */}
        <div className="flex-1 overflow-auto">
          {fieldSearchTerm && fieldsToDisplay.length === 0 ? (
            /* No Field Results */
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Search className="w-12 h-12 mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No fields found</h3>
              <p className="text-sm text-center max-w-md">
                No fields match "{fieldSearchTerm}". Try searching for terms like "deadline", "tuition", "early", "acceptance", or "test".
              </p>
              <button
                onClick={() => setFieldSearchTerm('')}
                className="mt-4 text-sm text-azure-600 hover:text-azure-700"
              >
                Clear field search
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200 min-w-[200px]">
                    College
                  </th>
                  {fieldsToDisplay.map(field => (
                    <th key={field.key} className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200 min-w-[120px] relative group">
                      <div>
                        {field.label}
                        {/* Tab indicator for field search */}
                        {fieldSearchTerm && field.tabLabel && (
                          <div className="text-xs text-azure-600 font-normal">
                            {field.tabIcon} {field.tabLabel}
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredColleges.map(college => {
                  const hasChanges = editingColleges.has(college.id);
                  
                  return (
                    <tr key={college.id} className={`hover:bg-slate-50 ${hasChanges ? 'bg-azure-50' : ''}`}>
                      {/* College Name & Category */}
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-slate-900 text-sm">{college.name}</div>
                          <div className="text-xs text-slate-400 capitalize">{college.category}</div>
                        </div>
                      </td>
                      
                      {/* Dynamic Fields */}
                      {fieldsToDisplay.map(field => (
                        <td key={field.key} className="px-4 py-3">
                          {renderFieldInput(college, field)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 flex justify-between items-center">
          <div className="text-sm text-slate-600">
            {filteredColleges.length} colleges • {editingColleges.size} with changes
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || editingColleges.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:bg-slate-300 transition-colors"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save {editingColleges.size} Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollegeBatchDataUpdate;